/**
 * Config Store
 * Manages user configuration with debounced save
 */
import { create } from 'zustand';
import { ENDPOINTS } from '@/feat/api-service-layer/endpoints';
import { BASE_URL } from '@/feat/api-service-layer/fetchClient';

interface ConfigStore {
  config: Record<string, unknown> | null;
  configError: boolean;
  saveTimeout: NodeJS.Timeout | null;

  // Actions
  setConfig: (config: Record<string, unknown>) => void;
  setConfigError: (error: boolean) => void;
  saveConfig: (getToken: () => Promise<string>) => Promise<void>;
  clearSaveTimeout: () => void;
}

export const useConfigStore = create<ConfigStore>((set, get) => ({
  config: null,
  configError: false,
  saveTimeout: null,

  setConfig: (config) => {
    set({ config, configError: false });
  },

  setConfigError: (error) => set({ configError: error }),

  clearSaveTimeout: () => {
    const { saveTimeout } = get();
    if (saveTimeout) {
      clearTimeout(saveTimeout);
      set({ saveTimeout: null });
    }
  },

  saveConfig: async (getToken) => {
    const { config, saveTimeout } = get();

    // Clear any pending saves
    if (saveTimeout) {
      clearTimeout(saveTimeout);
    }

    if (!config) {
      return;
    }

    // Debounce save - use requestIdleCallback if available, otherwise setTimeout
    const save = async () => {
      try {
        const token = await getToken();
        await fetch(`${BASE_URL}${ENDPOINTS.userSettings}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ settings: config }),
        });
      } catch (err) {
        console.error('Failed to save config:', err);
        set({ configError: true });
      }
    };

    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      const idleCallback = window.requestIdleCallback(save, { timeout: 2000 });
      set({ saveTimeout: idleCallback as unknown as NodeJS.Timeout });
    } else {
      const timeout = setTimeout(save, 100);
      set({ saveTimeout: timeout });
    }
  },
}));
